/**
 * @fileoverview The filter state a link into an agent digest has to name, and the query string that
 * carries it. Shared by the side nav's subtype links, the "Back to Agents" route and the digest's
 * own URL cleanup, so none of them can drift into scoping the same list differently.
 * @owner @Appboy/agent-console
 */

// eslint-disable-next-line local-eslint-rules/no-direct-react-table-import-access
import type { ColumnFilter } from "@tanstack/react-table";
import qs from "qs";

import type { InteractionSite } from "./types";

/** A column filter set; a real `ColumnFiltersState` (and readonly views of it) satisfies this. */
type ColumnFilters = ReadonlyArray<ColumnFilter>;

/**
 * The status scope every agent digest opens in: everything a workspace still works with, and
 * nothing it has archived. Also the digest's own default filter set, which is why it lives here
 * rather than beside the table hook that applies it -- a link that scopes a digest has to name the
 * same set the digest would have defaulted to.
 */
export const defaultAgentListFilters = [{ id: "status", value: ["active", "draft"] }];

/**
 * Serializes a column-filter set into the query string an agent digest URL carries.
 *
 * The spelling is the one the table's own URL writer produces -- qs bracket notation,
 * `columnFilters[<id>]` for a scalar value and `columnFilters[<id>][<n>]` per element for an array
 * one -- because the digest parses whatever reaches it with the same parser either way. The writer
 * (`getSearchParamsFactoryV2`) lives in the table service, which @lib may not import
 * (enforce-namespace-modularity), and in any case maps a whole table state to a
 * `start`/`limit`/`sortby` params object rather than one filter set to a query string, so this is
 * a second implementation of one format; it stays one function here
 * rather than three call sites so agent console URLs at least cannot disagree among themselves. It
 * does not reproduce the writer's habit of dropping filters with falsy values, which only matters
 * for a filter set that has one.
 *
 * Bracket keys are left literal rather than percent-encoded, matching how every hand-written agent
 * console link has spelled them (and what a test asserts on the "Back to Agents" href). That is
 * only how the address bar reads: the parser re-encodes the query before parsing it, so the literal
 * and encoded spellings resolve to the same filter set.
 */
export const agentDigestFilterQuery = (columnFilters: ColumnFilters): string =>
  qs.stringify(
    { columnFilters: Object.fromEntries(columnFilters.map(({ id, value }) => [id, value])) },
    { encodeValuesOnly: true }
  );

/**
 * Query string for a link into an agent digest narrowed to one interaction site.
 *
 * It names the default status scope alongside the site, and has to: a URL naming any column filter
 * replaces the digest's whole filter set rather than merging into it (searchParamsToState), so a
 * link naming only the site would drop the status scope and open a list that includes archived
 * agents -- with the Status control reading empty and no hint that the link was what cleared it.
 */
export const agentDigestSubtypeQuery = (site: InteractionSite): string =>
  agentDigestFilterQuery([{ id: "interaction_site", value: String(site) }, ...defaultAgentListFilters]);
